import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { notEmptyDigitInputSecurity } from '@/utils/validation';
import { VerificationFormData } from '@/components/common';
import clsx from 'clsx';
import { useState } from 'react';
import { Button } from '../../buttons';
import { InputDigits } from '../../form/InputDigits';
import { Icon } from '../../Icon';
import styles from './EnterTheCode.module.css';

interface EnterTheCodeProps {
  phoneNumber?: string;
  className?: string;
  onRetry?: () => void;
  onFormSubmit?: (values: VerificationFormData) => void;
  onChangeNumber?: () => void;
  isPopup?: boolean;
  isLoading?: boolean;
}

export interface EnterTheCodeFormData {
  digits: string;
}

export const EnterTheCode = ({
  phoneNumber,
  onRetry,
  className,
  onFormSubmit,
  onChangeNumber,
  isPopup = true,
  isLoading,
}: EnterTheCodeProps) => {
  const {
    handleSubmit,
    control,
    formState: { isDirty, isValid },
  } = useForm<EnterTheCodeFormData>({
    resolver: zodResolver(notEmptyDigitInputSecurity),
    defaultValues: { digits: '' },
    mode: 'onTouched',
  });

  const [isReCalling, setIsReCalling] = useState(false);

  function onSubmit(data: EnterTheCodeFormData) {
    onFormSubmit?.(data);
  }

  function onRecall() {
    if (isReCalling) return;
    onRetry?.();
    setIsReCalling(true);
    setTimeout(() => {
      setIsReCalling(false);
    }, 15000);
  }

  return (
    <form className={clsx(styles.container, className)} onSubmit={handleSubmit(onSubmit)}>
      {isPopup && (
        <Button
          variants="subtle"
          size="mediumSubtle"
          icon="chevronLeft"
          className={styles.buttonBack}
          iconClassName={styles.buttonBackIcon}
        />
      )}
      <h3 className={styles.title}>Enter the code</h3>
      <p className={styles.description}>
        We’ve sent a 6-digit code via SMS to <span className={styles.phoneNumber}>{phoneNumber}</span>. Please, enter
        this code to verify your mobile number.
      </p>
      {!isPopup && onChangeNumber && (
        <Button variants="subtle" size="mediumSubtle" className="mb-4" onPress={onChangeNumber}>
          Use a different number
        </Button>
      )}
      <div className={styles.circle}>
        <div className={styles.innerCircle} />
        <Icon name="phone" size={24} className={styles.phoneIcon} />
      </div>
      <Controller
        name="digits"
        control={control}
        render={({ field }) => <InputDigits {...field} className={styles.input} />}
      />
      <Button
        type="submit"
        size="large"
        className={styles.button}
        isDisabled={!isDirty || !isValid}
        loading={isLoading}
      >
        Continue
      </Button>
      <Button
        variants="subtle"
        size="mediumSubtle"
        icon={isReCalling ? 'check' : 'retry'}
        iconClassName={styles.icon}
        onPress={onRecall}
      >
        {isReCalling ? 'Sending SMS...' : 'Resend code'}
      </Button>
    </form>
  );
};
